import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import { closeGlobalPopup, fetchBill, openGlobalPopup, saveBill, resetSavedBill } from "../../redux/action";
import SmsPopup from "../../component/SMS/SmsPopup"
import { toast } from "../../Utills/toast";
import { createLiveBillPublisher } from '../../Utills/liveBillConnection';
import "./BillPage.css";
import PrintBill from "../../component/PrintBill/PrintBill";
import PaymentAllocationEditor, { createPaymentAllocation, getPaymentValidationError, summarizePayments } from "../../component/Billing/PaymentAllocationEditor";
import { calculateBillTotals } from "../../Utills/billingTaxCalculator";
import { useTranslation } from "react-i18next";
import GlobalPopup from "../../component/Popup/GlobalPopup";
import { ButtonLoader } from "../../component/common/Loader";
import { useOnlineStatus } from "../../Utills/useOnlineStatus";
import { useTrackedPrint } from "../../Utills/useTrackedPrint";
import { usePaymentMethodRequirement } from "../../Utills/usePaymentMethodRequirement";
import { getCheckedInReservationsForTableAPI } from "../../redux/api";


const TableBillPage = ({ tableOtp }) => {
  const requirePaymentMethod = usePaymentMethodRequirement();
  const online = useOnlineStatus();
  const { t } = useTranslation();
  const [showSmsPopup, setShowSmsPopup] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [discountExpanded, setDiscountExpanded] = useState(false);
  const [appliedServiceCharge, setAppliedServiceCharge] = useState(true);
  const [OrderType, setOrderType] = useState("DINE_IN");
  const [payments, setPayments] = useState([createPaymentAllocation(0)]);
  const [discountType, setDiscountType] = useState("");
  const [appliedDiscountType, setAppliedDiscountType] = useState("");
  const [complimentaryItems, setComplimentaryItems] = useState(() => new Set());
  const [voidReason, setVoidReason] = useState("");
  const [eligibleReservations, setEligibleReservations] = useState([]);
  const [selectedReservationId, setSelectedReservationId] = useState("");

  const componentRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const billPublisher = useRef(null);
  const { otp, tableName } = useParams();
  const currentTableOtp = tableOtp || otp;
  const info = JSON.parse(localStorage.getItem("admin_token_info")) || {};
  const roleId = Number(info.roleId);
  const billDetails = useSelector((state) => state?.admin.bill);
  const savedBill = useSelector((state) => state.admin.savedBill);  
  const loading = useSelector(state => state.admin.loading);

// 1️⃣ SignalR Connection for refect service charge and discount in self order customer potal bill
  useEffect(() => {
    if (!info.restaurantId || !currentTableOtp) return;
    const publisher = createLiveBillPublisher(info.restaurantId, currentTableOtp, () => {
      toast.warning("Live bill update is waiting for connection. Keep this page open to retry.", { toastId: "live-bill-pending" });
    });
    billPublisher.current = publisher;
    return () => { publisher.stop(); billPublisher.current = null; };
  }, [info.restaurantId, currentTableOtp]);

  const broadcastModification = (updatedDiscount, isServiceChargeOn) => {
    billPublisher.current?.publish({
      discountAmount: updatedDiscount,
      isServiceChargeApplied: isServiceChargeOn
    });
  };

  const handleApplyDiscount = () => {
    const discountValue = Number(discount) || 0;
    if (discountValue > subTotal) {
      toast.info("Discount cannot be greater than the subtotal.");
      return;
    }
    setAppliedDiscount(discountValue);
    setAppliedDiscountType(discountType.trim());
    setDiscountExpanded(false);
    
    // 🚀 Broadcast to Customer phone instantly
    broadcastModification(discountValue, appliedServiceCharge);
  };

  const handleRemoveDiscount = () => {
    setDiscount(0);
    setDiscountType("");
    setAppliedDiscountType("");
    setAppliedDiscount(0);
    setDiscountExpanded(false);
    broadcastModification(0, appliedServiceCharge);
  };

  const handleCancelDiscount = () => {
    setDiscount(appliedDiscount);
    setDiscountType(appliedDiscountType);
    setDiscountExpanded(false);
  };

  const handleServiceChargeToggle = (isChecked) => {
    setAppliedServiceCharge(isChecked);
    // 🚀 Broadcast to Customer phone instantly
    broadcastModification(appliedDiscount, isChecked);
  };

  useEffect(() => {
    dispatch(fetchBill(otp));
  }, [dispatch]);

  useEffect(() => {
    if (savedBill?.billId) {
      dispatch(closeGlobalPopup());
      setVoidReason("");
      handlePrint();
      //navigate("/tables"); 
    }
  }, [savedBill]);

  useEffect(() => {
    if (!tableName) return;
    getCheckedInReservationsForTableAPI(tableName).then(setEligibleReservations).catch(() => setEligibleReservations([]));
  }, [tableName]);

  const billingItems = (billDetails?.orders || []).map((item, index) => ({ ...item, isComplimentary: complimentaryItems.has(`${item.menuId}-${index}`) }));

  // const handleApplyDiscount = () => {
  //   const discountValue = Number(discount) || 0;

  //   // Prevent discount greater than subtotal
  //   if (discountValue > subTotal) {
  //     toast.info("Discount cannot be greater than Subtotal");
  //     return;
  //   }
  //   setAppliedDiscount(discountValue);
  // };


  const { cgst = 0, sgst = 0, serviceCharge = 0, showCGST = 0, showSGST = 0, showServiceCharge } = billDetails.charges || [];
  const calculated = calculateBillTotals({ items: billingItems, settings: billDetails.charges || {}, orderMode: OrderType, discount: appliedDiscount, applyServiceCharge: appliedServiceCharge });
  const { subtotal: subTotal, cgstAmount, sgstAmount, serviceChargeAmount, grandTotal, roundOff, payableAmount, packagingCharge, deliveryCharge } = calculated;
  const selectedReservation = eligibleReservations.find(item => item.reservationId === selectedReservationId);
  const reservationDepositApplied = Math.min(Number(selectedReservation?.availableDeposit || 0), payableAmount);
  const remainingPayable = Math.max(0, Number((payableAmount - reservationDepositApplied).toFixed(2)));
  const deliveryOptions = [
    { label: "Dine In", value: "DINE_IN" },
    { label: "Pick Up", value: "PICK_UP" },
    { label: "Delivery", value: "DELIVERY" },
  ];

  const paymentSummary = summarizePayments(payments);

  const handlePrint = useTrackedPrint({
    contentRef: componentRef, documentType: "BILL", getDocumentId: () => savedBill?.billId || tableName,
    onAfterPrint: () => {
      dispatch(resetSavedBill());
      navigate("/tables");
    }
  });

   const handlePrintAndNotNavigate = useTrackedPrint({
    contentRef: componentRef, documentType: "BILL", getDocumentId: () => savedBill?.billId || tableName,
    onAfterPrint: () => {
      dispatch(resetSavedBill());
    }
  });

  const handleSendSMS = () => {
    // Placeholder: integrate with SMS API (like Twilio, MSG91, etc.)
    // toast.info(`Bill for Order #${billDetails.menuId} sent via SMS!`);
  };

  const data = (isPaid, status) => ({
    tableNumber: tableName,
    subTotal: subTotal,
    serviceChargePercent: serviceCharge,
    serviceChargeAmount: serviceChargeAmount,
    isServiceChargeApplied: appliedServiceCharge,
    cGSTPercent: cgst,
    cGSTAmount: cgstAmount,
    sGSTPercent: sgst,
    sGSTAmount: sgstAmount,
    packagingCharge,
    deliveryCharge,
    discountAmount: appliedDiscount,
    discountType: discountType,
    grandTotal: grandTotal,
    settlementAmount: paymentSummary.settlementAmount,
    changeReturn: paymentSummary.changeReturn,
    paymentMode: paymentSummary.paymentMode,
    orderMode: OrderType,
    isPaid: isPaid,
    billStatus: status
  });


  const handleSaveBill = (status) => {
    if (status === "Void") {
      dispatch(openGlobalPopup("void-table-bill"));
      return;
    }
    const paymentError = status === "Paid" && remainingPayable > 0 ? getPaymentValidationError(payments, remainingPayable, requirePaymentMethod) : null;
    if (paymentError) {
      toast.error(paymentError);
      return false;
    }

    if (status === "Paid") {
      const paidData = data(true, status);

      dispatch(saveBill(paidData, billingItems.map(item => ({ ...item, foodOrderId: item.foodOrderId || item.orderId || null })), 0, currentTableOtp, remainingPayable > 0 ? paymentSummary.payments : [], null, selectedReservationId || null))
    }
  };

  const submitVoid = (event) => {
    event.preventDefault();
    const reason = voidReason.trim();
    if (!reason || loading) return;
    const voidData = data(false, "Void");
    dispatch(saveBill(voidData,
      billingItems.map(item => ({ ...item, foodOrderId: item.foodOrderId || item.orderId || null })),
      0, currentTableOtp, [], reason));
  };

  return (
    <div className="bill-page table-bill-page">
      <div className="bill-card">
        <p><strong>Table:</strong> {tableName}</p>
        <table className="bill-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>{t("Billing")}</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {billingItems.map((item, i) => (
              <tr key={i}>
                <td>
                  {item.name}
                  {item.addonSummary && (
                        <small style={{ color: "#6b7280", display: "block", fontSize: "11px" }}>
                          {item.addonSummary}
                        </small>
                )}
                </td>
                <td><label className="complimentary-toggle"><input type="checkbox" checked={item.isComplimentary} onChange={() => setComplimentaryItems((current) => { const key = `${item.menuId}-${i}`; const next = new Set(current); next.has(key) ? next.delete(key) : next.add(key); return next; })} /> {t("Complimentary")}</label></td>
                <td>{item.quantity}</td>
                <td>₹{item.price}</td>
                <td>₹{item.isComplimentary ? "0.00" : (item.quantity * item.price).toFixed(2)}</td>
              </tr>
            ))}

          </tbody>
          <tfoot>


            <tr>
              <td colSpan="4">Sub Total</td>
              <td>₹{subTotal.toFixed(2)}</td>
            </tr>

            {serviceChargeAmount > 0 && (
              <tr>
                <td colSpan="4">Service Charge ({calculated.serviceChargePercent}%)</td>
                <td>₹{serviceChargeAmount.toFixed(2)}</td>
              </tr>
            )}

            {cgstAmount > 0 && (
              <tr>
                <td colSpan="4">CGST</td>
                <td>₹{cgstAmount.toFixed(2)}</td>
              </tr>
            )}

            {sgstAmount > 0 && (
              <tr>
                <td colSpan="4">SGST</td>
                <td>₹{sgstAmount.toFixed(2)}</td>
              </tr>
            )}

            {appliedDiscount > 0 && (
              <tr>
                <td colSpan="4">Discount</td>
                <td>- ₹{appliedDiscount.toFixed(2)}</td>
              </tr>
            )}


            {packagingCharge > 0 && <tr><td colSpan="4">Packaging</td><td>₹{packagingCharge.toFixed(2)}</td></tr>}
            {deliveryCharge > 0 && <tr><td colSpan="4">Delivery</td><td>₹{deliveryCharge.toFixed(2)}</td></tr>}
            <tr>
              <td colSpan="4"><strong>Grand Total</strong></td>
              <td><strong>₹{grandTotal.toFixed(2)}</strong></td>
            </tr>

            <tr>
              <td colSpan="4">round off</td>
              <td>{roundOff}</td>
            </tr>
            <tr>
              <td className="bill-total" colSpan="4"><strong>Payable Amount</strong></td>
              <td><strong>₹{payableAmount.toFixed(2)}</strong></td>
            </tr>

          </tfoot>
        </table>

        <div className="bill-mobile-items" aria-label="Bill items">
          {billingItems.map((item, i) => (
            <article className="bill-mobile-item" key={`${item.menuId || item.name}-${i}`}>
              <div className="bill-mobile-item__heading">
                <div>
                  <strong>{item.name}</strong>
                  {item.addonSummary && <small>{item.addonSummary}</small>}
                </div>
                <strong>₹{item.isComplimentary ? "0.00" : (item.quantity * item.price).toFixed(2)}</strong>
              </div>
              <div className="bill-mobile-item__details">
                <span>{item.quantity} × ₹{Number(item.price || 0).toFixed(2)}</span>
                <label className="complimentary-toggle">
                  <input type="checkbox" checked={item.isComplimentary} onChange={() => setComplimentaryItems((current) => { const key = `${item.menuId}-${i}`; const next = new Set(current); next.has(key) ? next.delete(key) : next.add(key); return next; })} />
                  {t("Complimentary")}
                </label>
              </div>
            </article>
          ))}
        </div>

        <section className="bill-mobile-summary" aria-label="Bill summary">
          <div><span>Sub Total</span><strong>₹{subTotal.toFixed(2)}</strong></div>
          {serviceChargeAmount > 0 && <div><span>Service Charge ({calculated.serviceChargePercent}%)</span><strong>₹{serviceChargeAmount.toFixed(2)}</strong></div>}
          {cgstAmount > 0 && <div><span>CGST</span><strong>₹{cgstAmount.toFixed(2)}</strong></div>}
          {sgstAmount > 0 && <div><span>SGST</span><strong>₹{sgstAmount.toFixed(2)}</strong></div>}
          {appliedDiscount > 0 && <div><span>Discount</span><strong>− ₹{appliedDiscount.toFixed(2)}</strong></div>}
          {packagingCharge > 0 && <div><span>Packaging</span><strong>₹{packagingCharge.toFixed(2)}</strong></div>}
          {deliveryCharge > 0 && <div><span>Delivery</span><strong>₹{deliveryCharge.toFixed(2)}</strong></div>}
          <div><span>Grand Total</span><strong>₹{grandTotal.toFixed(2)}</strong></div>
          <div><span>Round off</span><strong>{roundOff}</strong></div>
          <div className="bill-mobile-summary__payable"><span>Payable Amount</span><strong>₹{payableAmount.toFixed(2)}</strong></div>
        </section>

        {/* <div className="bill-actions">
          <button className="btn-primary" onClick={handlePrint}>🖨️ Print Bill</button>
          <button className="btn-primary" onClick={handlePrint}>🖨️ Print and End Session</button>
          <button className="btn-secondary" onClick={() => setShowSmsPopup(true)}>📩 Send via SMS</button>
          <button className="btn-secondary">🧾 Download PDF</button>
          <button className="btn-red">💳 End Session</button>
        </div> */}

        <div className="cart-footer" style={{ padding: "10px 10px 10px 10px" }}>
          <div className="cart-payment-type">

            <div className="cart-delivery-options">

              {deliveryOptions.map((option) => (
                <span
                  key={option.value}
                  className={OrderType === option.value ? "active" : ""}
                  onClick={() => setOrderType(option.value)}
                >
                  {option.label}
                </span>
              ))}

            </div>

            <label className="pay-option paid">
              <input
                type="checkbox"
                disabled={!showServiceCharge && !billDetails.charges?.dineInServiceChargeEnabled && !billDetails.charges?.pickupServiceChargeEnabled && !billDetails.charges?.deliveryServiceChargeEnabled}
                checked={appliedServiceCharge}
                onChange={(e) => handleServiceChargeToggle(e.target.checked)}
              />
              <span>Service charge ({calculated.serviceChargePercent}%)</span>
            </label>

          </div>

          <section className="mobile-discount" aria-label="Discount">
            {appliedDiscount > 0 && !discountExpanded ? (
              <div className="mobile-discount__applied">
                <div>
                  <span>Discount applied</span>
                  <strong>−₹{appliedDiscount.toFixed(2)}</strong>
                  {appliedDiscountType && <small>{appliedDiscountType}</small>}
                </div>
                <div className="mobile-discount__actions">
                  <button type="button" onClick={() => setDiscountExpanded(true)}>Edit</button>
                  <button type="button" className="mobile-discount__remove" onClick={handleRemoveDiscount}>Remove</button>
                </div>
              </div>
            ) : !discountExpanded ? (
              <button type="button" className="mobile-discount__toggle" onClick={() => setDiscountExpanded(true)}>
                + Add discount <span>(optional)</span>
              </button>
            ) : (
              <div className="mobile-discount__form">
                <div className="mobile-discount__heading">
                  <strong>{appliedDiscount > 0 ? "Edit discount" : "Add discount"}</strong>
                  <span>Optional</span>
                </div>
                <label>
                  Discount Amount
                  <input type="number" min="0" step="0.01" placeholder="Enter discount amount" value={discount === 0 ? "" : discount}
                    onChange={(e) => setDiscount(e.target.value)} />
                </label>
                <label>
                  Discount Reason / Type
                  <input type="text" value={discountType} placeholder="Offer, coupon or platform"
                    onChange={(e) => setDiscountType(e.target.value)} />
                </label>
                <div className="mobile-discount__form-actions">
                  <button type="button" className="mobile-discount__cancel" onClick={handleCancelDiscount}>Cancel</button>
                  <button type="button" onClick={handleApplyDiscount} disabled={Number(discount) <= 0}>Apply Discount</button>
                </div>
              </div>
            )}
          </section>

          {eligibleReservations.length > 0 && <section className="reservation-deposit-selector">
            <label htmlFor="reservation-deposit">Reservation deposit</label>
            <select id="reservation-deposit" value={selectedReservationId} disabled={loading} onChange={event => setSelectedReservationId(event.target.value)}>
              <option value="">Do not apply a reservation deposit</option>
              {eligibleReservations.map(item => <option key={item.reservationId} value={item.reservationId}>{item.customerName} · {item.numberOfGuests} guests · ₹{Number(item.availableDeposit).toFixed(2)} available</option>)}
            </select>
            {selectedReservation && <div className="reservation-deposit-summary"><span>Deposit applied</span><strong>− ₹{reservationDepositApplied.toFixed(2)}</strong><small>Remaining to collect: ₹{remainingPayable.toFixed(2)}</small></div>}
          </section>}

          {remainingPayable > 0 ? <PaymentAllocationEditor payableAmount={remainingPayable} payments={payments} onChange={setPayments} disabled={loading} required={requirePaymentMethod} /> : <div className="reservation-deposit-covered">The reservation deposit covers the full payable amount.</div>}

          <div className="settlement">
            <label className="settlement-label">Discount Amount</label>
            <input
              type="number"
              className="settlement-input"
              placeholder="Enter discount amount"
              value={discount}
              onChange={(e) => setDiscount(e.target.value)}
            />

            <label className="settlement-label">Discount Type</label>
            <input
              type="text"
              className="settlement-input"
              value={discountType}
              placeholder="Offer/Coupon/Zomato/Swiggy"
              onChange={(e) => setDiscountType(e.target.value)}
            />

            <button className="settlement-btn" onClick={handleApplyDiscount}>
              Apply
            </button>

            

          </div>

          <div className="cart-action-button">
            <span
              className="cart-button"
              onClick={() => online && handleSaveBill('Paid')}
              aria-disabled={loading || !online}
              title={!online ? "Internet connection is required to settle a bill." : undefined}
              style={{ pointerEvents: loading || !online ? "none" : "auto", opacity: loading || !online ? 0.6 : 1 }}
            >Paid</span>
            {[1, 2].includes(roleId) && <span
              className="cart-button"
              onClick={() => online && handleSaveBill('Void')}
              aria-disabled={loading || !online}
              title={!online ? "Internet connection is required to void a bill." : undefined}
              style={{ pointerEvents: loading || !online ? "none" : "auto", opacity: loading || !online ? 0.6 : 1 }}
            >Void</span>}
             <span
              className="cart-button"
              onClick={() => handlePrintAndNotNavigate()}
              style={{ pointerEvents: loading ? "none" : "auto", opacity: loading ? 0.6 : 1 }}
            >Only Print Bill</span>
          </div>

        </div>
      </div>
      {/* Popup */}
      {showSmsPopup && (
        <SmsPopup billId={billDetails.id} onClose={() => setShowSmsPopup(false)}
        />
      )}

      <GlobalPopup
        popupKey="void-table-bill"
        title={t("Void bill")}
        onClose={() => setVoidReason("")}
        disableClose={loading}
        footer={(closePopup) => <>
          <button type="button" className="popup-action-btn secondary" onClick={closePopup} disabled={loading}>{t("Cancel")}</button>
          <button type="submit" form="void-table-bill-form" className="popup-action-btn"
            disabled={loading || !online || !voidReason.trim()} title={!online ? "Internet connection is required to void a bill." : undefined} aria-busy={loading}>
            {loading && <span className="popup-button-spinner" aria-hidden="true" />}
            {loading ? <ButtonLoader label={t("Processing...")} /> : t("Confirm void")}
          </button>
        </>}
      >
        <form id="void-table-bill-form" className="void-bill-form" onSubmit={submitVoid}>
          <label>
            <span>{t("Reason")}</span>
            <textarea autoFocus required disabled={loading} value={voidReason}
              onChange={event => setVoidReason(event.target.value)} />
          </label>
        </form>
      </GlobalPopup>

      <PrintBill 
        ref={componentRef}
        OrderAndChargesDetails={billDetails}
        tableName={tableName}
        subTotal={subTotal}
        cgstAmount={cgstAmount}
        sgstAmount={sgstAmount}
        serviceChargeAmount={serviceChargeAmount}
        packagingCharge={packagingCharge}
        deliveryCharge={deliveryCharge}
        appliedDiscount={appliedDiscount}
        grandTotal={grandTotal}
        orderMode={OrderType}
        billDetails={savedBill}
        roundOff={roundOff}
        payableAmount={payableAmount}
      />
    </div>
  );
};

export default TableBillPage;
